
import {
    computed,
    defineComponent,
    watch,
    reactive,
    ref,
    Ref,
    onBeforeUnmount,
    PropType,
    onBeforeMount,
} from 'vue'

import draggable from 'vuedraggable'

// Components
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'

//
import TemplatesActions from '@/modules/CreateOrderModule/components/TemplatesActions/index.vue'
import SendersListItem from '@/modules/CreateOrderModule/components/SendersListItem/index.vue'
import ReceiversListItem from '@/modules/CreateOrderModule/components/ReceiversListItem/index.vue'
import StepAdditionalServicesFTL from '@/modules/CreateOrderModule/components/StepAdditionalServicesFTL/index.vue'

// Realisations
import { senders } from '@/core/realisations/senders.realisation'
import { receivers } from '@/core/realisations/receivers.realisation'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Types
import { SenderPayloadT, SenderT } from '@/core/types/senders.types'
import { ReceiverPayloadT, ReceiverT } from '@/core/types/receivers.types'
import { TemplateT } from '@/core/types/templates.types'
import { FTLBidWaypointT, NewWaypointT } from '@/core/types/ftl_bids.types'
import { CompletedAdditionalServicesT } from '@/core/types/create_order.types'

// Composable
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'

import { useDebounce } from 'vue-composable'

// constants
import { EMPTY_ADDITIONAL_SERVICES } from '@/core/constants/additional_services.constants'

export default defineComponent({
    components: {
        draggable,
        TheButton,
        TheInput,
        Loader,
        Cheap,
        TemplatesActions,
        SendersListItem,
        ReceiversListItem,
        'step-additional-services-ftl': StepAdditionalServicesFTL,
    },
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        isWaypoints: {
            type: Boolean,
            default: false,
        },
        companyId: {
            type: Number,
            default: null,
        },
        opened: {
            type: Boolean,
            default: false,
        },
        completed: {
            type: Boolean,
            default: false,
        },
        isIntracity: {
            type: Boolean,
            default: false,
        },
        ftlType: {
            type: Number,
            default: null,
        },
        waypoints: {
            type: Array as PropType<FTLBidWaypointT[]>,
            default: null,
        },
    },
    emits: ['submit', 'update'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            selected_templates: [] as any[],
            search_query: null as string | null,
            drag: false,
        })

        const isSenderSelected = computed(() => state.selected_templates.length > 0)
        const isInsideCity = computed(() => props.ftlType == 2)

        const data_state = reactive({
            senders: new DataState<SenderT>({}),
            receivers: new DataState<ReceiverT>({}),
        })

        const loadSenders = async (page = 1) => {
            data_state.senders.pagination.current_page = page

            try {
                data_state.senders.is_loading = true
                const response = await senders.getList(page, props.companyId)
                data_state.senders.list = response.data.senders

                data_state.senders.pagination.total_pages = response.data.total_pages

                data_state.senders.is_failed = false
                data_state.senders.failure.error_state.is_cancel = false
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При получении отправителей произошла ошибка',
                })
                data_state.senders.failure.catchError(error)

                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_failed = true
                }
            } finally {
                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_loading = false
                }
            }
        }

        const resetSenders = () => {
            data_state.senders.is_loading = false
            data_state.senders.is_failed = false
            data_state.senders.failure.error_state.is_cancel = false
            data_state.senders.list = []
            data_state.senders.pagination.current_page = 1
            data_state.senders.pagination.total_pages = 1
        }

        const loadMoreSenders = async (page: number) => {
            if (page > data_state.senders.pagination.total_pages) return
            data_state.senders.pagination.current_page = page

            try {
                data_state.senders.is_loading = true

                const response = await senders.getList(page, props.companyId)
                data_state.senders.list = data_state.senders.list.concat(response.data.senders)
                data_state.senders.pagination.total_pages = response.data.total_pages

                data_state.senders.is_failed = false
                data_state.senders.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.senders.failure.catchError(error)

                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_failed = true
                }
            } finally {
                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_loading = false
                }
            }
        }

        const loadReceivers = async (page = 1) => {
            data_state.receivers.pagination.current_page = page

            try {
                data_state.receivers.is_loading = true

                let response = null

                if (isInsideCity.value) {
                    let city_id = state.selected_templates[0].city_id

                    if (props.isIntracity) {
                        response = await receivers.getListFtl(page, props.companyId, city_id)
                    } else {
                        response = await receivers.getListWithoutCityId(
                            page,
                            props.companyId,
                            city_id
                        )
                    }
                } else {
                    if (props.isIntracity) {
                        response = await receivers.getListFtl(page, props.companyId)
                    } else {
                        response = await receivers.getListWithoutCityId(page, props.companyId)
                    }
                }

                if (response) {
                    data_state.receivers.list = response.data.receivers
                    data_state.receivers.pagination.total_pages = response.data.total_pages
                }

                data_state.receivers.is_failed = false
                data_state.receivers.failure.error_state.is_cancel = false
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При получении получателей произошла ошибка',
                })
                data_state.receivers.failure.catchError(error)

                if (!data_state.receivers.failure.error_state.is_cancel) {
                    data_state.receivers.is_failed = true
                }
            } finally {
                if (!data_state.receivers.failure.error_state.is_cancel) {
                    data_state.receivers.is_loading = false
                }
            }
        }

        const resetReceivers = () => {
            data_state.receivers.is_loading = false
            data_state.receivers.is_failed = false
            data_state.receivers.failure.error_state.is_cancel = false
            data_state.receivers.list = []
            data_state.receivers.pagination.current_page = 1
            data_state.receivers.pagination.total_pages = 1
        }

        const loadMoreReceivers = async (page: number) => {
            if (page > data_state.receivers.pagination.total_pages) return
            data_state.receivers.pagination.current_page = page

            try {
                data_state.receivers.is_loading = true

                let response = null

                if (isInsideCity.value) {
                    let city_id = state.selected_templates[0].city_id

                    if (props.isIntracity) {
                        response = await receivers.getListFtl(page, props.companyId, city_id)
                    } else {
                        response = await receivers.getListWithoutCityId(
                            page,
                            props.companyId,
                            city_id
                        )
                    }
                } else {
                    if (props.isIntracity) {
                        response = await receivers.getListFtl(page, props.companyId)
                    } else {
                        response = await receivers.getListWithoutCityId(page, props.companyId)
                    }
                }

                if (response) {
                    data_state.receivers.list = data_state.receivers.list.concat(
                        response.data.receivers
                    )
                    data_state.receivers.pagination.total_pages = response.data.total_pages
                }

                data_state.receivers.is_failed = false
                data_state.receivers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.receivers.failure.catchError(error)

                if (!data_state.receivers.failure.error_state.is_cancel) {
                    data_state.receivers.is_failed = true
                }
            } finally {
                if (!data_state.receivers.failure.error_state.is_cancel) {
                    data_state.receivers.is_loading = false
                }
            }
        }

        const searchSender = async () => {
            data_state.senders.pagination.current_page = 1
            data_state.senders.pagination.total_pages = 1
            data_state.senders.list = []

            if (!state.search_query || state.search_query === '') {
                await loadSenders(1)
                return
            }

            try {
                data_state.senders.is_loading = true

                const response = await senders.searchSender(state.search_query, props.companyId)
                data_state.senders.list = response.data

                data_state.senders.is_failed = false
                data_state.senders.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.senders.failure.catchError(error)

                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_failed = true
                }
            } finally {
                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_loading = false
                }
            }
        }

        const searchReceiver = async () => {
            data_state.receivers.pagination.current_page = 1
            data_state.receivers.pagination.total_pages = 1
            data_state.receivers.list = []

            if (!state.search_query || state.search_query === '') {
                loadReceivers(1)
                return
            }

            try {
                data_state.receivers.is_loading = true

                if (!isInsideCity.value) {
                    const response = await receivers.searchReceiver({
                        param: state.search_query,
                        city_id: senderCityId.value,
                        company_id: props.companyId,
                    })

                    data_state.receivers.list = response.data.receivers
                }

                if (isInsideCity.value) {
                    const response = await receivers.searchReceiver({
                        param: state.search_query,
                        dispatcher_sector_id: senderCityDispatcherId.value,
                        company_id: props.companyId,
                    })

                    data_state.receivers.list = response.data.receivers
                }

                data_state.receivers.is_failed = false
                data_state.receivers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.receivers.failure.catchError(error)

                if (!data_state.receivers.failure.error_state.is_cancel) {
                    data_state.receivers.is_failed = true
                }
            } finally {
                if (!data_state.receivers.failure.error_state.is_cancel) {
                    data_state.receivers.is_loading = false
                }
            }
        }

        const onLoadMoreTemplates = () => {
            if (isSenderSelected.value) {
                loadMoreReceivers(data_state.receivers.pagination.current_page + 1)
            } else {
                loadMoreSenders(data_state.senders.pagination.current_page + 1)
            }
        }

        const onTemplateSearch = useDebounce(() => {
            if (isSenderSelected.value) {
                searchReceiver()
            } else {
                searchSender()
            }
        }, 800)

        watch(
            () => props.opened,
            (opened) => {
                if (opened) {
                    if (isSenderSelected.value) {
                        loadReceivers(1)
                    } else {
                        loadSenders(1)
                    }
                }
            }
        )

        const mapWaypointsToTemplates = (waypoint: FTLBidWaypointT): TemplateT => {
            let is_sender = waypoint.number === 0

            if (is_sender) {
                let sender = waypoint.sender_template as SenderT

                return {
                    id: sender.id,
                    city: sender.city,
                    city_id: sender.city_id,
                    comment: sender.comment,
                    country: sender.country,
                    country_id: sender.country_id,
                    full_address: sender.full_address,
                    full_name: sender.full_name,
                    house: sender.house,
                    index: sender.index,
                    latitude: sender.latitude,
                    longitude: sender.longitude,
                    office: sender.office,
                    phone: sender.phone,
                    additional_phone: sender.additional_phone,
                    street: sender.street,
                    title: sender.title,
                    entity: sender.title,
                    warehouse_id: sender.warehouse_id,
                    distribution_center_id: null,
                }
            }

            let receiver = waypoint.receiver_template as ReceiverT

            return {
                id: receiver.id,
                city: receiver.city,
                city_id: receiver.city_id,
                comment: receiver.comment,
                country: receiver.country,
                country_id: receiver.country_id,
                full_address: receiver.full_address,
                full_name: receiver.full_name,
                house: receiver.house,
                index: receiver.index,
                latitude: receiver.latitude,
                longitude: receiver.longitude,
                office: receiver.office,
                phone: receiver.phone,
                additional_phone: receiver.additional_phone,
                street: receiver.street,
                title: receiver.title,
                entity: receiver.title,
                warehouse_id: receiver.warehouse_id ? receiver.warehouse_id : null,
                distribution_center_id: receiver.distribution_center_id
                    ? receiver.distribution_center_id
                    : null,
            }
        }

        const everyWaypointHaveReceiverTemplate = (waypoint: FTLBidWaypointT) => {
            if (waypoint.number === 0) return true
            if (waypoint.receiver_template) return true
            return false
        }

        const presetWaypoints = () => {
            if (props.waypoints && props.waypoints.length) {
                if (
                    props.waypoints[0] &&
                    props.waypoints[0].sender_template &&
                    props.waypoints.every(everyWaypointHaveReceiverTemplate)
                ) {
                    state.selected_templates = props.waypoints.map(mapWaypointsToTemplates)

                    if (isSenderSelected.value) {
                        loadReceivers(1)
                    } else {
                        loadSenders(1)
                    }
                } else {
                    if (isSenderSelected.value) {
                        loadReceivers(1)
                    } else {
                        loadSenders(1)
                    }
                }
            }
        }

        onBeforeMount(presetWaypoints)

        const onTemplateSelect = (template: TemplateT) => {
            const templateCopy = { ...template, additional_service: {} }

            if (state.selected_templates.length === 0) {
                state.selected_templates.push(templateCopy)

                resetSenders()
                loadReceivers(1)

                state.search_query = null
                return
            }

            state.selected_templates.push(templateCopy)
            state.search_query = null
        }

        const onSelectedTemplateDelete = (template_index: number) => {
            if (template_index === 0) {
                const answer = confirm(
                    'При удалении отправного пункта остальные пункты будут сброшены, продолжить?'
                )
                if (!answer) return

                state.selected_templates = []

                resetSenders()
                resetReceivers()

                loadSenders(1)
                return
            }

            const answer = confirm('Вы действительно хотите удалить выбранный шаблон?')
            if (!answer) return
            state.selected_templates.splice(template_index, 1)
        }

        const onTemplateDeleted = () => {
            if (isSenderSelected.value) {
                loadReceivers(data_state.receivers.pagination.current_page)
            } else {
                loadSenders(data_state.senders.pagination.current_page)
            }
        }

        const onTemplateCreate = async (payload: SenderPayloadT | ReceiverPayloadT) => {
            try {
                actions_state.is_actions_loading = true

                if (!isSenderSelected.value) {
                    await senders.createSender(payload)
                    notifications.notify({ title: 'Отправитель создан', type: 'success' })
                    await loadSenders(1)
                } else {
                    await receivers.createReceiver(payload)
                    notifications.notify({ title: 'Получатель создан', type: 'success' })
                    await loadReceivers(1)
                }

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)

                if (!isSenderSelected.value) {
                    notifications.notifyError({ error, title: 'Ошибка при создании отправителя' })
                } else {
                    notifications.notifyError({ error, title: 'Ошибка при создании получателя' })
                }
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onTemplateUpdate = async (payload: SenderPayloadT | ReceiverPayloadT) => {
            if (!payload.id) return

            try {
                actions_state.is_actions_loading = true

                if (!isSenderSelected.value) {
                    await senders.updateSender(payload.id, payload)
                    notifications.notify({ title: 'Отправитель отредактирован', type: 'success' })
                    await loadSenders(1)
                } else {
                    await receivers.updateReceiver(payload.id, payload)
                    notifications.notify({ title: 'Получатель отредактирован', type: 'success' })
                    await loadReceivers(1)
                }

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)

                if (!isSenderSelected.value) {
                    notifications.notifyError({
                        error,
                        title: 'Ошибка при редактировании отправителя',
                    })
                } else {
                    notifications.notifyError({
                        error,
                        title: 'Ошибка при редактировании получателя',
                    })
                }
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const isFailedState = computed(
            () => data_state.senders.is_failed || data_state.receivers.is_failed
        )
        const isLoadingState = computed(
            () => data_state.senders.is_loading || data_state.receivers.is_loading
        )

        const isEmptyState = computed(() => {
            if (
                !isSenderSelected.value &&
                !data_state.senders.is_loading &&
                data_state.senders.list.length === 0
            )
                return true
            if (
                isSenderSelected.value &&
                !data_state.receivers.is_loading &&
                data_state.receivers.list.length === 0
            )
                return true

            return false
        })

        const checkIfTemplateSelected = (template_id: number) => {
            const maybe_finded = state.selected_templates.find(
                (selected_template) => selected_template.id === template_id
            )
            return Boolean(maybe_finded)
        }

        const { actions_state, startCreate, startUpdate } = useActions<TemplateT>()

        const senderCityId = computed(() => {
            if (state.selected_templates.length === 0) return null
            return state.selected_templates[0].city_id
        })

        const senderCityDispatcherId = computed(() => {
            if (state.selected_templates.length === 0) return null
            return state.selected_templates[0].dispatcher_sector_id
        })

        const receiverCityId = computed(() => {
            if (state.selected_templates.length === 0) return null
            if (!isSenderSelected.value) return null
            if (!isInsideCity.value) return null

            return state.selected_templates[0].city_id
        })

        const templatewindow: Ref<HTMLElement | null> = ref(null)

        const handleScroll = () => {
            if (!templatewindow.value || (!isSenderSelected.value && data_state.senders.is_loading))
                return
            if (isSenderSelected.value && data_state.receivers.is_loading) return

            const scrollHeight = Math.floor(templatewindow.value.scrollHeight)
            const scrollTop = Math.floor(templatewindow.value.scrollTop)
            const offsetHeight = Math.floor(templatewindow.value.offsetHeight)

            if (scrollTop >= scrollHeight - offsetHeight) {
                onLoadMoreTemplates()
            }
        }

        onBeforeUnmount(() => {
            if (!templatewindow.value) return
            templatewindow.value.removeEventListener('scroll', handleScroll)
        })

        watch(
            () => templatewindow.value,
            (templatewindow_newest) => {
                if (templatewindow_newest) {
                    if (!templatewindow.value) return
                    templatewindow.value.addEventListener('scroll', handleScroll)
                }
            }
        )

        const onDragEnd = () => {
            state.drag = false
        }

        const onDragMove = (event: any) => {
            const context = event.draggedContext

            if (context.index === 0 || context.futureIndex === 0) {
                return false
            }
        }

        const getWaypoints = (): NewWaypointT[] => {
            return state.selected_templates.map((waypoint, index) => {
                return {
                    template_id: waypoint.id,
                    city_id: waypoint.city_id,
                    is_endpoint: index === state.selected_templates.length - 1 ? 1 : 0,
                    number: index,
                    additional_service: waypoint.additional_service || {
                        ...EMPTY_ADDITIONAL_SERVICES,
                    },
                }
            })
        }

        const onSubmit = () => {
            if (state.selected_templates.length < 2) {
                notifications.notify({
                    title: 'Выбор пунктов',
                    description:
                        'Для заверешния выбора пунктов укажите отправной и конечный пункты',
                    type: 'danger',
                })
                return
            }

            const waypoints = getWaypoints()
            emit('submit', [...waypoints])
        }

        const onUpdate = () => {
            emit('update')
        }

        const onAdditionalServicesSubmit = (
            additional_service: CompletedAdditionalServicesT,
            index: number
        ) => {
            if (props.completed) {
                state.selected_templates[index].additional_service = additional_service
                onSubmit()

                return
            }

            state.selected_templates[index].additional_service = additional_service
        }

        const isSubmitDisabled = computed(
            () =>
                state.selected_templates.length < 2 ||
                !state.selected_templates.every((template) => template.additional_service)
        )

        const getInitialServices = (index: number) => {
            if (props.waypoints && props.waypoints[index]) {
                return props.waypoints[index].additional_service
            } else {
                return null
            }
        }

        return {
            state,
            isSenderSelected,
            isInsideCity,
            data_state,
            actions_state,
            startCreate,
            startUpdate,
            //
            onTemplateSelect,
            onSelectedTemplateDelete,
            onTemplateDeleted,
            onTemplateCreate,
            onTemplateUpdate,
            onTemplateSearch,
            onLoadMoreTemplates,
            //
            isFailedState,
            isLoadingState,
            isEmptyState,
            //
            checkIfTemplateSelected,
            senderCityId,
            receiverCityId,
            //
            templatewindow,
            //
            onDragEnd,
            onDragMove,
            //
            onSubmit,
            onUpdate,
            //
            onAdditionalServicesSubmit,
            isSubmitDisabled,
            getInitialServices,
        }
    },
})
